import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { paymentsApi } from '../api/payments';
import { invoicesApi } from '../api/invoices';
import { confirm } from '../components/ConfirmDialog';
import { useRole } from '../context/AuthContext';

const fmt = (n: number) => '₹' + (n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MODE_LABELS: Record<string, string> = { cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer', cheque: 'Cheque', other: 'Other' };

export default function PaymentsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { canDelete } = useRole();
  const { data: payments = [], isLoading } = useQuery({ queryKey: ['payments'], queryFn: paymentsApi.list, staleTime: 30 * 1000 });
  const { data: invoicesResult } = useQuery({ queryKey: ['invoices'], queryFn: () => invoicesApi.list({}, 1, 200), staleTime: 30 * 1000 });
  const invoices = invoicesResult?.data ?? [];
  const del = useMutation({
    mutationFn: ({ invoiceId, id }: { invoiceId: string; id: string }) => paymentsApi.delete(invoiceId, id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['payments'] }); qc.invalidateQueries({ queryKey: ['invoices'] }); qc.refetchQueries({ type: 'all' }); },
  });

  const invoiceMap = Object.fromEntries(invoices.map(i => [i.id, i]));
  const total = payments.reduce((s, p) => s + p.amount, 0);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Payments Received</h1>
        <div style={{ background: 'var(--surface)', borderRadius: 8, padding: '0.5rem 1rem', border: '1px solid var(--border)', fontWeight: 700, color: 'var(--success)' }}>
          Total: {fmt(total)}
        </div>
      </div>
      <div className="card">
        {isLoading ? <div className="loading-overlay"><span className="spinner" /></div>
          : payments.length === 0 ? <div className="empty-state"><div className="empty-state-icon">💳</div><div className="empty-state-text">No payments recorded yet</div><div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>Record payments from the invoice detail page</div></div>
          : (
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Party</th>
                  <th>Mode</th>
                  <th>Reference</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {[...payments].sort((a, b) => b.paymentDate.localeCompare(a.paymentDate)).map(p => {
                  const inv = invoiceMap[p.invoiceId];
                  return (
                    <tr key={p.id}>
                      <td>{p.paymentDate}</td>
                      <td>
                        {inv ? (
                          <span style={{ color: 'var(--primary)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline dotted' }}
                            onClick={() => navigate(`/invoices/${inv.id}`)}>
                            {inv.invoiceNumber || '(draft)'}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: '0.82rem' }}>{p.invoiceId.slice(0, 8)}…</span>
                        )}
                      </td>
                      <td style={{ color: 'var(--text-2)' }}>{inv?.partyName ?? '—'}</td>
                      <td><span className="badge badge-blue">{MODE_LABELS[p.mode] ?? p.mode}</span></td>
                      <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{p.reference ?? '—'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--success)' }}>{fmt(p.amount)}</td>
                      <td>
                        {canDelete && <button className="btn btn-danger btn-sm" disabled={del.isPending} onClick={async () => {
                          const inv = invoiceMap[p.invoiceId];
                          if (await confirm({ message: `Delete payment of ${fmt(p.amount)}${inv ? ` for ${inv.invoiceNumber}` : ''}?`, danger: true }))
                            del.mutate({ invoiceId: p.invoiceId, id: p.id });
                        }}>Del</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}
